import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SignOut, Plus, X } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/Chrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["NEW LEAD", "QUALIFIED", "CONSULTATION BOOKED", "PROPOSAL", "WON", "LOST"];

function Login({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem("hights_token", data.token);
            onLogin(data.token);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-base px-5" data-testid="admin-login">
            <form onSubmit={submit} className="w-full max-w-sm border border-white/12 bg-surface p-8 md:p-10">
                <Logo />
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">CRM — admin access</p>
                <label className="mb-2 mt-8 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45" htmlFor="ad-email">Email</label>
                <input id="ad-email" data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="h-12 w-full border border-white/15 bg-black px-4 text-sm text-white focus:border-white/45 focus:outline-none" placeholder="admin@hights.co" />
                <label className="mb-2 mt-5 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45" htmlFor="ad-pass">Password</label>
                <input id="ad-pass" data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="h-12 w-full border border-white/15 bg-black px-4 text-sm text-white focus:border-white/45 focus:outline-none" placeholder="••••••••" />
                {error && <p className="mt-4 text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
                <button type="submit" disabled={busy} data-testid="admin-login-button"
                    className="mt-7 w-full bg-white py-3.5 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/85 disabled:opacity-50">
                    {busy ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </div>
    );
}

function LeadCard({ lead, onUpdate }) {
    return (
        <article className="border border-white/12 bg-black p-4" data-testid={`lead-card-${lead.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-white">{lead.name}</p>
                    <p className="text-xs text-white/45">{lead.business || "—"}</p>
                </div>
                <span className="border border-white/20 px-2 py-1 font-mono text-[10px] text-white/70" data-testid={`lead-score-${lead.id}`}>{lead.score}</span>
            </div>
            <p className="mt-2 text-xs text-white/50">{lead.email}{lead.phone ? ` · ${lead.phone}` : ""}</p>
            {lead.main_challenge && <p className="mt-2 line-clamp-2 text-xs font-light text-white/40">{lead.main_challenge}</p>}
            <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">{lead.source} · {new Date(lead.created_at).toLocaleDateString("en-GB")}</p>
            <select
                value={lead.status}
                onChange={(e) => onUpdate(lead.id, e.target.value)}
                data-testid={`lead-status-${lead.id}`}
                className="mt-3 h-9 w-full border border-white/15 bg-black px-2 text-xs text-white focus:border-white/45 focus:outline-none"
            >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
        </article>
    );
}

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem("hights_token"));
    const [leads, setLeads] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [showTestimonial, setShowTestimonial] = useState(false);
    const [t, setT] = useState({ name: "", business: "", result: "", quote: "" });

    const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

    const load = useCallback(async () => {
        try {
            const [l, b] = await Promise.all([
                axios.get(`${API}/enquiries`, auth()),
                axios.get(`${API}/bookings`, auth()),
            ]);
            setLeads(l.data);
            setBookings(b.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("hights_token");
                setToken(null);
            }
        }
    }, [auth]);

    useEffect(() => { if (token) load(); }, [token, load]);

    const updateStatus = async (id, status) => {
        try {
            const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, auth());
            setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
            toast.success(`Lead moved to ${status}`);
        } catch {
            toast.error("Update failed");
        }
    };

    const addTestimonial = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/testimonials`, t, auth());
            toast.success("Testimonial published to the site.");
            setShowTestimonial(false);
            setT({ name: "", business: "", result: "", quote: "" });
        } catch {
            toast.error("Could not save testimonial");
        }
    };

    if (!token) return (<><SEO title="HIGHTS CRM — Sign in" description="HIGHTS CRM admin." path="/admin" /><Login onLogin={setToken} /></>);

    const avg = leads.length ? Math.round(leads.reduce((s, l) => s + l.score, 0) / leads.length) : 0;

    return (
        <div className="min-h-screen bg-base" data-testid="admin-dashboard">
            <SEO title="HIGHTS CRM — Dashboard" description="HIGHTS CRM admin dashboard." path="/admin" />
            <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl">
                <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5">
                    <div className="flex items-baseline gap-3">
                        <Logo />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">CRM</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowTestimonial(true)} data-testid="add-testimonial-button"
                            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white">
                            <Plus size={13} /> Testimonial
                        </button>
                        <button onClick={() => { localStorage.removeItem("hights_token"); setToken(null); }} data-testid="admin-logout-button"
                            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white">
                            <SignOut size={13} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1600px] px-5 py-10">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="crm-stats">
                    {[["Total leads", leads.length], ["Avg. score", avg], ["Consultations", bookings.length], ["Won", leads.filter((l) => l.status === "WON").length]].map(([label, v]) => (
                        <div key={label} className="border border-white/12 bg-surface p-5">
                            <p className="font-display text-3xl font-black text-white">{v}</p>
                            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">{label}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 flex gap-4 overflow-x-auto pb-4" data-testid="crm-board" data-lenis-prevent>
                    {STATUSES.map((status) => (
                        <div key={status} className="w-72 shrink-0">
                            <p className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                {status}
                                <span className="text-white/30">{leads.filter((l) => l.status === status).length}</span>
                            </p>
                            <div className="space-y-3 border-t border-white/15 pt-3">
                                {leads.filter((l) => l.status === status).map((lead) => (
                                    <LeadCard key={lead.id} lead={lead} onUpdate={updateStatus} />
                                ))}
                                {leads.filter((l) => l.status === status).length === 0 && (
                                    <p className="border border-dashed border-white/10 p-4 text-center text-[10px] text-white/25">No leads</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 border border-white/12 bg-surface p-6 md:p-8" data-testid="bookings-panel">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Consultation bookings</p>
                    <div className="mt-5 divide-y divide-white/10">
                        {bookings.length === 0 && <p className="py-4 text-sm text-white/35">No bookings yet.</p>}
                        {bookings.map((b) => (
                            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`booking-row-${b.id}`}>
                                <div>
                                    <p className="text-sm font-bold text-white">{b.name} {b.business ? `· ${b.business}` : ""}</p>
                                    <p className="text-xs text-white/45">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                                </div>
                                <p className="font-mono text-xs text-white/70">{b.date} — {b.time}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            {showTestimonial && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm" onClick={() => setShowTestimonial(false)}>
                    <form onSubmit={addTestimonial} onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/15 bg-surface p-8" data-testid="testimonial-form">
                        <div className="flex items-center justify-between">
                            <p className="font-display text-lg font-bold text-white">Publish testimonial</p>
                            <button type="button" onClick={() => setShowTestimonial(false)} aria-label="Close" data-testid="testimonial-close"><X size={18} className="text-white/60" /></button>
                        </div>
                        <p className="mt-2 text-xs font-light text-white/45">Only publish verified, client-approved testimonials.</p>
                        {[["name", "Client name"], ["business", "Business"], ["result", "Result (e.g. +40% enquiries)"]].map(([k, ph]) => (
                            <input key={k} required value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} placeholder={ph} data-testid={`testimonial-${k}`}
                                className="mt-4 h-11 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none" />
                        ))}
                        <textarea required rows={3} value={t.quote} onChange={(e) => setT({ ...t, quote: e.target.value })} placeholder="Testimonial quote" data-testid="testimonial-quote"
                            className="mt-4 w-full border border-white/15 bg-black px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none" />
                        <button type="submit" data-testid="testimonial-submit" className="mt-5 w-full bg-white py-3 text-xs font-bold uppercase tracking-[0.25em] text-black hover:bg-white/85">Publish</button>
                    </form>
                </div>
            )}
        </div>
    );
}
